import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, faPlus, faUsers, faDroplet, faLink, faPlugCircleXmark,
  faQrcode, faTrash, faCheckCircle, faTimesCircle, faInfoCircle,
  faCreditCard, faCircle, faTriangleExclamation, faXmark, faSpinner
} from '@fortawesome/free-solid-svg-icons';

const Subaccounts = ({ userInfo }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [agencyConnected, setAgencyConnected] = useState(false);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [showDripModal, setShowDripModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [dripSettings, setDripSettings] = useState({
    dripModeEnabled: false,
    dripModeMessagesPerBatch: 20,
    dripModeDelayBetweenMessages: 5000,
    dripModeDelayBetweenBatches: 300000,
  });

  useEffect(() => {
    fetchLocations();
    const urlParams = new URLSearchParams(window.location.search);
    const agencyTokenStatus = urlParams.get('agency_token');
    if (agencyTokenStatus === 'success') {
      setSuccessMessage('Agency access token generated successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setSuccessMessage(''), 5000);
    } else if (agencyTokenStatus === 'failed') {
      setError('Failed to generate agency token. Please try again.');
      setTimeout(() => setError(''), 5000);
    }
  }, [userInfo]);

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const agencyId = userInfo?.agency?.id;
      if (!agencyId) {
        setError('No agency found for user');
        setLoading(false);
        return;
      }
      const response = await fetch(`/agencies/${agencyId}/locations`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
        setError('');
        setSubscriptionRequired(false);
        setSubscriptionError(null);
      } else if (response.status === 403) {
        const errorData = await response.json();
        if (errorData.code === 'SUBSCRIPTION_REQUIRED' || errorData.code === 'SUBSCRIPTION_INACTIVE' ||
            errorData.code === 'TRIAL_EXPIRED' || errorData.code === 'SUBSCRIPTION_EXPIRED') {
          setSubscriptionRequired(true);
          setSubscriptionError(errorData);
          setLocations([]);
        } else {
          setError(errorData.message || 'Access denied');
        }
      } else {
        setError('Failed to fetch locations');
        setLocations([]);
      }
      const agencyResponse = await fetch(`/agencies/${agencyId}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (agencyResponse.ok) {
        const agencyData = await agencyResponse.json();
        const settings = agencyData.settings || {};
        setAgencyConnected(!!(settings.agencyAccessToken || settings.ghlCompanyId));
      }
    } catch (err) {
      setError('Network error');
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  if (subscriptionRequired) {
    return (
      <div className="space-y-6">
        <div className="glass-card p-8 text-center border border-yellow-200 bg-yellow-50/80">
          <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
            <FontAwesomeIcon icon={faCreditCard} className="text-yellow-600 text-2xl" />
          </div>
          <h2 className="text-2xl font-bold text-yellow-800 mb-2">Plan Required</h2>
           <p className="text-yellow-700 mb-6">
             {subscriptionError?.message || 'An active plan is needed to manage locations.'}
           </p>
           <button
             onClick={() => window.location.href = '/dashboard/billing'}
             className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-3 px-6 rounded-xl transition-colors"
           >
             View Plans
           </button>
           <p className="text-sm text-yellow-600 mt-4">Unlock WhatsApp routing for all your GHL locations.</p>
        </div>
      </div>
    );
  }

  const connectWhatsApp = async (locationId) => {
    window.location.href = `/connect-whatsapp/${locationId}`;
  };

  const generateQRCode = async (locationId) => {
    const qrUrl = `${window.location.origin}/whatsapp/qr-latest/${locationId}`;
    window.open(qrUrl, '_blank');
  };

  const disconnectWhatsApp = async (locationId) => {
    if (!window.confirm('Are you sure you want to disconnect WhatsApp? You will need to scan the QR code again to reconnect.')) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/whatsapp/disconnect', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ locationId })
      });
      if (response.ok) { alert('WhatsApp disconnected successfully'); fetchLocations(); }
      else { alert('Failed to disconnect WhatsApp'); }
    } catch (err) { alert('Network error while disconnecting WhatsApp'); }
  };

  const deleteLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this subaccount? This action cannot be undone.')) return;
    try {
      const token = localStorage.getItem('access_token');
      const agencyId = userInfo?.agency?.id;
      const response = await fetch(`/agencies/${agencyId}/locations/${locationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) { alert('Subaccount deleted successfully'); fetchLocations(); }
      else { alert('Failed to delete subaccount'); }
    } catch (err) { alert('Network error while deleting subaccount'); }
  };

  const connectGHL = () => {
    const agencyId = userInfo?.agency?.id;
    const userId = userInfo?.user?.id;
    if (!agencyId) { window.location.href = `/oauth/authorize`; return; }
    window.location.href = `/oauth/ghl/install?agencyId=${agencyId}&user=${userId}`;
  };

  const generateAgencyAccessToken = () => {
    const agencyId = localStorage.getItem('agencyId');
    const backendUrl = process.env.REACT_APP_API_URL || '';
    window.location.href = `${backendUrl}/oauth/ghl/authorize-agency?agencyId=${agencyId}`;
  };

  const openDripModal = async (location) => {
    setSelectedLocation(location);
    setDripSettings({
      dripModeEnabled: location.dripModeEnabled || false,
      dripModeMessagesPerBatch: location.dripModeMessagesPerBatch || 20,
      dripModeDelayBetweenMessages: location.dripModeDelayBetweenMessages || 5000,
      dripModeDelayBetweenBatches: location.dripModeDelayBetweenBatches || 300000,
    });
    setShowDripModal(true);
  };

  const saveDripSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const agencyId = userInfo?.agency?.id;
      const response = await fetch(`/agencies/${agencyId}/locations/${selectedLocation.ghlLocationId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(dripSettings)
      });
      if (response.ok) { alert('Drip mode settings saved successfully'); setShowDripModal(false); fetchLocations(); }
      else { alert('Failed to save drip mode settings'); }
    } catch (err) { alert('Network error while saving drip settings'); }
  };

  const toggleAutoCreateContacts = async (location) => {
    const newValue = !location.autoCreateContacts;
    try {
      const token = localStorage.getItem('access_token');
      const agencyId = userInfo?.agency?.id;
      const response = await fetch(`/agencies/${agencyId}/locations/${location.ghlLocationId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ autoCreateContacts: newValue })
      });
      if (response.ok) {
        setLocations(locations.map(loc =>
          loc.ghlLocationId === location.ghlLocationId ? { ...loc, autoCreateContacts: newValue } : loc
        ));
        setSuccessMessage(newValue ? 'Auto-create contacts enabled' : 'Auto-create contacts disabled');
        setTimeout(() => setSuccessMessage(''), 4000);
      } else { alert('Failed to update auto-create contacts setting'); }
    } catch (err) { alert('Network error while updating setting'); }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FontAwesomeIcon icon={faSpinner} className="text-4xl text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl flex items-center justify-between">
          <span className="flex items-center gap-2">
            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
            {successMessage}
          </span>
          <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl flex items-center justify-between">
          <span className="flex items-center gap-2">
            <FontAwesomeIcon icon={faTriangleExclamation} className="text-red-500" />
            {error}
          </span>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">Linked Locations</h2>
           <p className="mt-1 text-sm text-gray-500">Manage your GHL locations and WhatsApp channels</p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button onClick={() => setShowAddModal(true)} className="btn-primary inline-flex items-center gap-2">
            <FontAwesomeIcon icon={faPlus} />
             Link Location
           </button>
         </div>
       </div>

       {/* Locations Grid */}
       {locations.length === 0 ? (
         <div className="text-center py-16 glass-card">
           <div className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
             <FontAwesomeIcon icon={faBuilding} className="text-indigo-600 text-3xl" />
           </div>
           <h3 className="mt-2 text-lg font-semibold text-gray-900">No locations linked</h3>
           <p className="mt-1 text-sm text-gray-500">Connect your first GHL location to start routing messages.</p>
           <div className="mt-6">
             <button onClick={() => setShowAddModal(true)} className="btn-primary inline-flex items-center gap-2">
               <FontAwesomeIcon icon={faPlus} />
               Link Location
            </button>
          </div>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
          style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 50%, #f0f9ff 100%)', borderRadius: '1rem', padding: '1.5rem' }}
        >
          {locations.map((location) => (
            <div
              key={location.ghlLocationId}
              className="glass-card rounded-2xl overflow-hidden"
              style={{ backdropFilter: 'blur(12px)', background: 'rgba(255,255,255,0.82)', border: '1px solid rgba(255,255,255,0.5)', boxShadow: '0 8px 32px rgba(30,27,75,0.10)' }}
            >
              {/* Card Header Strip */}
               <div className="px-5 py-4 flex items-center gap-3" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #3730a3 100%)' }}>
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <FontAwesomeIcon icon={faBuilding} className="text-white text-lg" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{location.name || 'Unnamed Location'}</p>
                   <p className="text-xs text-indigo-200 truncate">ID: {location.ghlLocationId}</p>
                </div>
              </div>

              {/* Card Body */}
              <div className="px-5 py-4 space-y-3">
                {/* WhatsApp Status */}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">WhatsApp</span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                    location.whatsappStatus === 'connected'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    <FontAwesomeIcon icon={faCircle} className={`text-xs ${location.whatsappStatus === 'connected' ? 'text-green-500' : 'text-gray-400'}`} />
                    {location.whatsappStatus === 'connected' ? 'Connected' : 'Not Connected'}
                  </span>
                </div>

                {/* Mode */}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Mode</span>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                    location.whatsappSharingMode === 'individual'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {location.whatsappSharingMode === 'individual' ? 'Individual' : 'Shared'}
                  </span>
                </div>

                {/* Auto-Create Contacts Toggle */}
                <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50/80">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-gray-700">Auto-Create Contacts</span>
                    <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 text-xs" title="Automatically create new contacts in GHL when receiving messages from unknown numbers" />
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleAutoCreateContacts(location); }}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      location.autoCreateContacts !== false ? 'bg-primary-500' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      location.autoCreateContacts !== false ? 'translate-x-5' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Card Actions */}
              <div className="px-5 pb-5 space-y-2">
                <button
                  onClick={() => window.location.href = `/user-management/${location.ghlLocationId}`}
                  className="btn-primary w-full text-sm flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faUsers} />
                  Manage Users
                </button>
                <button
                  onClick={() => openDripModal(location)}
                  className="btn-secondary w-full text-sm flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faDroplet} />
                   Throttle Settings
                </button>
                {location.whatsappStatus !== 'connected' ? (
                  <button
                    onClick={() => connectWhatsApp(location.ghlLocationId)}
                    className="btn-secondary w-full text-sm flex items-center justify-center gap-2"
                  >
                    <FontAwesomeIcon icon={faLink} />
                     Link WhatsApp
                  </button>
                ) : (
                  <button
                    onClick={() => disconnectWhatsApp(location.ghlLocationId)}
                    className="btn-danger w-full text-sm flex items-center justify-center gap-2"
                  >
                    <FontAwesomeIcon icon={faPlugCircleXmark} />
                     Unlink WhatsApp
                  </button>
                )}
                <button
                  onClick={() => generateQRCode(location.ghlLocationId)}
                  className="btn-secondary w-full text-sm flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faQrcode} />
                   Pairing QR Code
                </button>
                <button
                  onClick={() => deleteLocation(location.ghlLocationId)}
                  className="btn-danger w-full text-sm flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faTrash} />
                   Remove Location
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Location Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-6 w-full max-w-md glass-card rounded-2xl shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FontAwesomeIcon icon={faPlus} className="text-primary-600" />
                 Link New Location
              </h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <FontAwesomeIcon icon={faXmark} className="text-xl" />
              </button>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
              <div className="flex gap-3">
                <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 text-lg flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-blue-800 mb-1">How it works:</h4>
                  <ol className="text-sm text-blue-700 space-y-1 list-decimal pl-4">
                     <li>Click the button below to authorize a GHL location.</li>
                     <li>Choose GHLWA as the conversation provider in the authorization screen.</li>
                     <li>A QR pairing link will be automatically added to the location.</li>
                  </ol>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => { setShowAddModal(false); connectGHL(); }}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faLink} />
                 Authorize GHL Location
              </button>
              <button onClick={() => setShowAddModal(false)} className="btn-secondary w-full">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Drip Mode Settings Modal */}
      {showDripModal && selectedLocation && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-6 w-full max-w-2xl glass-card rounded-2xl shadow-2xl my-8">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FontAwesomeIcon icon={faDroplet} className="text-blue-500" />
                 Message Throttling — {selectedLocation.name}
              </h3>
              <button onClick={() => setShowDripModal(false)} className="text-gray-400 hover:text-gray-600">
                <FontAwesomeIcon icon={faXmark} className="text-xl" />
              </button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
              <div className="flex gap-3">
                <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 text-lg flex-shrink-0 mt-0.5" />
                <div>
                   <h4 className="text-sm font-semibold text-blue-800">What is Message Throttling?</h4>
                   <p className="text-sm text-blue-700 mt-1">Throttling spaces out messages to keep your WhatsApp channel safe. Instead of bulk-sending, messages are queued and delivered at controlled intervals.</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                   <label className="text-sm font-semibold text-gray-900">Enable Throttling</label>
                   <p className="text-xs text-gray-500 mt-0.5">Space out delivery to protect your channel</p>
                </div>
                <button
                  onClick={() => setDripSettings({...dripSettings, dripModeEnabled: !dripSettings.dripModeEnabled})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    dripSettings.dripModeEnabled ? 'bg-primary-500' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    dripSettings.dripModeEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-semibold text-gray-900">Messages Per Batch</label>
                <input
                  type="number"
                  value={dripSettings.dripModeMessagesPerBatch}
                  onChange={(e) => setDripSettings({...dripSettings, dripModeMessagesPerBatch: parseInt(e.target.value)})}
                  className="input-field"
                  min="1" max="100"
                />
                <p className="text-xs text-gray-500">Number of messages to send before taking a longer break</p>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-semibold text-gray-900">Delay Between Messages (ms)</label>
                <input
                  type="number"
                  value={dripSettings.dripModeDelayBetweenMessages}
                  onChange={(e) => setDripSettings({...dripSettings, dripModeDelayBetweenMessages: parseInt(e.target.value)})}
                  className="input-field"
                  min="1000" max="60000" step="1000"
                />
                <p className="text-xs text-gray-500">Delay between each message ({(dripSettings.dripModeDelayBetweenMessages / 1000).toFixed(1)} seconds)</p>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-semibold text-gray-900">Delay Between Batches (ms)</label>
                <input
                  type="number"
                  value={dripSettings.dripModeDelayBetweenBatches}
                  onChange={(e) => setDripSettings({...dripSettings, dripModeDelayBetweenBatches: parseInt(e.target.value)})}
                  className="input-field"
                  min="60000" max="3600000" step="60000"
                />
                <p className="text-xs text-gray-500">Longer break after each batch ({(dripSettings.dripModeDelayBetweenBatches / 60000).toFixed(1)} minutes)</p>
              </div>

              {dripSettings.dripModeEnabled && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-green-800 mb-2">Summary</h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• Send {dripSettings.dripModeMessagesPerBatch} messages per batch</li>
                    <li>• Wait {(dripSettings.dripModeDelayBetweenMessages / 1000).toFixed(1)}s between each message</li>
                    <li>• Wait {(dripSettings.dripModeDelayBetweenBatches / 60000).toFixed(1)} min between batches</li>
                    <li>• Example: 100 messages ≈ {Math.ceil(100 / dripSettings.dripModeMessagesPerBatch)} batches over ~{((100 * dripSettings.dripModeDelayBetweenMessages + Math.ceil(100 / dripSettings.dripModeMessagesPerBatch) * dripSettings.dripModeDelayBetweenBatches) / 60000).toFixed(1)} min</li>
                  </ul>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={saveDripSettings} className="btn-primary flex-1 flex items-center justify-center gap-2">
                Save Settings
              </button>
              <button onClick={() => setShowDripModal(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subaccounts;
